/**
 * Modal Manager
 * Handles 3DS authentication modal
 */

import { ALLOWED_3DS_ORIGINS, DIMENSIONS, ELEMENT_IDS } from '../constants/sdk-constants';
import {
  get3DSContentStyles,
  get3DSHeaderStyles,
  get3DSIframeStyles,
  get3DSModalStyles,
  get3DSOverlayStyles,
  getCloseButtonStyles,
} from './styles';

export interface ModalConfig {
  environment: 'local' | 'sandbox' | 'production' | 'development' | 'preprod';
  debug?: boolean;
}

export class ModalManager {
  private config: ModalConfig;
  private static activeModal: {
    overlay: HTMLElement;
    sessionId: string;
    paymentId: string;
  } | null = null;

  constructor(config: ModalConfig) {
    this.config = config;
  }

  /**
   * Open 3DS authentication modal
   * Returns a Promise that resolves with the authentication result
   */
  open(challengeUrl: string, sessionId: string, paymentId: string): Promise<boolean> {
    if (this.config.debug) {
      console.log('[SDK] open3DSModal called with URL:', challengeUrl);
      console.log('[SDK] Session ID:', sessionId, 'Payment ID:', paymentId);
    }

    // If there's already a modal open, close it first
    if (ModalManager.activeModal) {
      console.warn('[SDK] Closing existing modal before opening new one');
      ModalManager.activeModal.overlay.remove();
      ModalManager.activeModal = null;
    }

    return new Promise((resolve) => {
      // Create overlay
      const overlay = document.createElement('div');
      overlay.id = ELEMENT_IDS.THREE_DS_OVERLAY;
      overlay.style.cssText = get3DSOverlayStyles();

      // Create modal
      const modal = document.createElement('div');
      modal.style.cssText = get3DSModalStyles();

      // Create header
      const header = document.createElement('div');
      header.style.cssText = get3DSHeaderStyles();
      header.innerHTML = `
        <h3 style="margin: 0; font-size: 18px; font-weight: 600;">Secure Payment Authentication</h3>
      `;

      // Create content with iframe
      const content = document.createElement('div');
      content.style.cssText = get3DSContentStyles();

      const iframe = document.createElement('iframe');
      iframe.src = challengeUrl;
      iframe.style.cssText = get3DSIframeStyles();
      iframe.setAttribute('allow', 'payment');
      iframe.setAttribute('sandbox', 'allow-forms allow-scripts allow-same-origin allow-popups');
      content.appendChild(iframe);

      // Assemble modal
      modal.appendChild(header);
      modal.appendChild(content);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);

      // Store as THE active modal
      ModalManager.activeModal = {
        overlay,
        sessionId,
        paymentId
      };

      // Message handler for 3DS completion
      const messageHandler = (event: MessageEvent) => {
        if (!event.data) return;

        // Validate origin
        if (!this.isAllowedOrigin(event.origin)) {
          if (this.config.debug) {
            console.warn('[SDK] Rejected 3DS message from unauthorized origin:', event.origin);
          }
          return;
        }

        const data = event.data;
        const is3DSComplete = data.type === 'THREE_DS_COMPLETE' || data.type === '3ds-complete';
        const isSuccess = data.status === 'success';
        const isFailure = data.status === 'failed' || data.status === 'failure';

        // Success case
        if (is3DSComplete && isSuccess) {
          overlay.remove();
          window.removeEventListener('message', messageHandler);
          resolve(true);
          return;
        }

        // Also handle legacy format
        if (isSuccess && !is3DSComplete) {
          overlay.remove();
          window.removeEventListener('message', messageHandler);
          resolve(true);
          return;
        }

        // Failure case
        if ((is3DSComplete && isFailure) || data.type === '3ds-failed' || isFailure) {
          overlay.remove();
          window.removeEventListener('message', messageHandler);
          resolve(false);
          return;
        }
      };

      window.addEventListener('message', messageHandler);
    });
  }

  /**
   * Close 3DS modal (called from external events like WebSocket)
   * @param sessionId - Optional session ID to validate before closing
   */
  static close(sessionId?: string): void {
    if (!ModalManager.activeModal) {
      // No modal is open - nothing to do
      console.log('[SDK] No active modal to close');
      return;
    }

    // If sessionId is provided, validate it matches the current modal
    if (sessionId && ModalManager.activeModal.sessionId !== sessionId) {
      console.warn(
        `[SDK] Session ID mismatch: webhook for ${sessionId}, but modal is for ${ModalManager.activeModal.sessionId}. Ignoring close request.`
      );
      return;
    }

    // Close the modal
    console.log('[SDK] Closing modal for session:', sessionId || ModalManager.activeModal.sessionId);
    ModalManager.activeModal.overlay.remove();
    ModalManager.activeModal = null;
  }

  /**
   * Check if a modal is currently open
   */
  static isModalOpen(): boolean {
    return ModalManager.activeModal !== null;
  }

  /**
   * Get current session ID
   */
  static getCurrentSessionId(): string | null {
    return ModalManager.activeModal?.sessionId || null;
  }

  /**
   * Check if origin is allowed for 3DS messages
   */
  private isAllowedOrigin(origin: string): boolean {
    // Check exact match with allowed origins
    if ((ALLOWED_3DS_ORIGINS as readonly string[]).includes(origin)) {
      return true;
    }

    // Allow localhost in dev/sandbox environments
    if (this.config.environment === 'sandbox' || this.config.environment === 'development') {
      if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
        return true;
      }
    }

    return false;
  }
}
